import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Row,
  Col,
  InputNumber,
  Switch,
  Button,
  Tag,
  Progress,
  Popconfirm,
  Spin,
  Tooltip
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarAlt,
  faChartLine,
  faQuestionCircle
} from '@fortawesome/free-solid-svg-icons';
import { DeleteOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import EventTypeService from '../../../services/EventTypeService';

const MONTHS = [
  { value: 1, label: 'January', short: 'Jan' },
  { value: 2, label: 'February', short: 'Feb' },
  { value: 3, label: 'March', short: 'Mar' },
  { value: 4, label: 'April', short: 'Apr' },
  { value: 5, label: 'May', short: 'May' },
  { value: 6, label: 'June', short: 'Jun' },
  { value: 7, label: 'July', short: 'Jul' },
  { value: 8, label: 'August', short: 'Aug' },
  { value: 9, label: 'September', short: 'Sep' },
  { value: 10, label: 'October', short: 'Oct' },
  { value: 11, label: 'November', short: 'Nov' },
  { value: 12, label: 'December', short: 'Dec' }
];

const MonthlyCapSection = ({ 
  eventTypeId,
  defaultMonthlyCapEnabled,
  defaultMonthlyCap,
  monthlyCaps = [],
  onDefaultCapChange,
  onMonthlyCapChange,
  isEditing = false 
}) => {
  const [monthlyStats, setMonthlyStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [localMonthlyCaps, setLocalMonthlyCaps] = useState(monthlyCaps || []);
  const [localDefaultEnabled, setLocalDefaultEnabled] = useState(defaultMonthlyCapEnabled || false);
  const [localDefaultCap, setLocalDefaultCap] = useState(defaultMonthlyCap || null);
  const debounceTimerRef = useRef(null);

  // Sync with props
  useEffect(() => {
    setLocalMonthlyCaps(monthlyCaps || []);
    setLocalDefaultEnabled(defaultMonthlyCapEnabled || false);
    setLocalDefaultCap(defaultMonthlyCap || null);
  }, [monthlyCaps, defaultMonthlyCapEnabled, defaultMonthlyCap]);

  // Load monthly stats when editing
  useEffect(() => {
    if (eventTypeId) {
      loadMonthlyStats();
    }
  }, [eventTypeId]);

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const loadMonthlyStats = useCallback(async () => {
    if (!eventTypeId) return;
    
    setLoadingStats(true);
    try {
      const response = await EventTypeService.getMonthlyStats(eventTypeId, 24);
      if (response.success) {
        setMonthlyStats(response.data.data || response.data);
      }
    } catch (error) {
      console.error('Error loading monthly stats:', error);
    } finally {
      setLoadingStats(false);
    }
  }, [eventTypeId]);

  // Debounced stats refresh - useful when user is typing
  const debouncedRefreshStats = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      loadMonthlyStats();
    }, 500);
  }, [loadMonthlyStats]);

  const handleDefaultEnabledChange = (enabled) => {
    setLocalDefaultEnabled(enabled);
    onDefaultCapChange?.(enabled, localDefaultCap);
  };

  const handleDefaultCapValueChange = (value) => {
    setLocalDefaultCap(value);
    onDefaultCapChange?.(localDefaultEnabled, value);
  };

  const handleAddMonthCap = () => {
    const currentMonth = moment().month() + 1;
    const currentYear = moment().year();
    
    // Find the next month that doesn't have a cap
    let month = currentMonth;
    let year = currentYear;
    
    while (localMonthlyCaps.some(cap => cap.month === month && cap.year === year)) {
      month++;
      if (month > 12) {
        month = 1;
        year++;
      }
      // Safety limit
      if (year > currentYear + 2) break;
    }

    const newCap = {
      month,
      year,
      maxBookings: 50,
      isEnabled: true
    };

    const updated = [...localMonthlyCaps, newCap];
    setLocalMonthlyCaps(updated);
    onMonthlyCapChange?.(updated);
    
    // Refresh stats to show data for the new month
    loadMonthlyStats();
  };

  const handleUpdateMonthCap = (index, field, value) => {
    const updated = [...localMonthlyCaps];
    updated[index] = { ...updated[index], [field]: value };
    setLocalMonthlyCaps(updated);
    onMonthlyCapChange?.(updated);
  };

  const handleRemoveMonthCap = (index) => {
    const updated = localMonthlyCaps.filter((_, i) => i !== index);
    setLocalMonthlyCaps(updated);
    onMonthlyCapChange?.(updated);
  };

  const getProgressColor = (percent) => {
    if (percent >= 100) return '#ff4d4f';
    if (percent >= 80) return '#faad14';
    return '#52c41a';
  };

  const getMonthStats = (month, year) => {
    if (!monthlyStats?.stats) return null;
    return monthlyStats.stats.find(s => s.month === month && s.year === year);
  };

  // Generate next 24 months for the dropdown, excluding months already used by other caps
  const getAvailableMonthOptions = (currentCapIndex = -1) => {
    const options = [];
    const now = moment();
    
    // Get months already used by other caps (not the current one being edited)
    const usedMonths = new Set(
      localMonthlyCaps
        .filter((_, idx) => idx !== currentCapIndex)
        .map(cap => `${cap.year}-${cap.month}`)
    );
    
    for (let i = 0; i < 24; i++) {
      const targetDate = now.clone().add(i, 'months');
      const month = targetDate.month() + 1;
      const year = targetDate.year();
      const monthName = MONTHS.find(m => m.value === month)?.label || '';
      const value = `${year}-${month}`;
      
      options.push({
        value,
        month,
        year,
        label: `${monthName} ${year}`,
        disabled: usedMonths.has(value)
      });
    }
    
    return options;
  };

  return (
    <div style={{ 
      marginTop: '24px', 
      padding: '20px', 
      background: '#f8f9fa', 
      borderRadius: '8px',
      border: '1px solid #e9ecef'
    }}>
      <h4 style={{ marginBottom: '16px', color: '#1a1a1a', fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '8px', color: '#1890ff' }} />
        Monthly Booking Caps
        <Tooltip 
          title={
            <div style={{ maxWidth: '300px' }}>
              <p style={{ margin: '0 0 8px 0', fontWeight: '600' }}>How Monthly Caps Work:</p>
              <p style={{ margin: '0 0 8px 0' }}><strong>Default Cap:</strong> Sets a baseline limit applied to ALL months that don&apos;t have a specific cap set.</p>
              <p style={{ margin: '0 0 8px 0' }}><strong>Month-Specific Caps:</strong> Override the default for individual months. Use these for months with different capacity needs.</p>
              <p style={{ margin: 0 }}><strong>Example:</strong> Set a default of 50/month, but limit February to only 20 due to reduced staff.</p>
            </div>
          }
          placement="right"
          overlayStyle={{ maxWidth: '350px' }}
        >
          <FontAwesomeIcon 
            icon={faQuestionCircle} 
            style={{ 
              color: '#8c8c8c', 
              cursor: 'help',
              fontSize: '14px'
            }} 
          />
        </Tooltip>
      </h4>
      
      <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#666' }}>
        Set limits on how many bookings can be made for this event type each month. 
        When a month&apos;s limit is reached, that month will show as unavailable on the public booking page.
      </p>

      {/* Default Monthly Cap */}
      <div style={{ 
        padding: '16px', 
        background: 'white', 
        borderRadius: '8px', 
        marginBottom: '16px',
        border: '1px solid #e8e8e8'
      }}>
        <Row align="middle" gutter={[16, 12]}>
          <Col xs={24} sm={12}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Switch
                checked={localDefaultEnabled}
                onChange={handleDefaultEnabledChange}
                size="small"
              />
              <span style={{ fontWeight: '500', color: '#1a1a1a' }}>
                Enable Default Monthly Cap
              </span>
            </div>
            <p style={{ margin: '4px 0 0 36px', fontSize: '12px', color: '#888' }}>
              Applied to all months without a specific cap
            </p>
          </Col>
          <Col xs={24} sm={12}>
            <InputNumber
              value={localDefaultCap}
              onChange={handleDefaultCapValueChange}
              min={1}
              max={10000}
              placeholder="e.g., 50"
              disabled={!localDefaultEnabled}
              style={{ width: '100%' }}
              addonAfter="bookings/month"
            />
          </Col>
        </Row>
      </div>

      {/* Month-Specific Caps */}
      <div style={{ 
        padding: '16px', 
        background: 'white', 
        borderRadius: '8px',
        border: '1px solid #e8e8e8'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontWeight: '500', color: '#1a1a1a' }}>
              Month-Specific Caps
            </span>
            {eventTypeId && (
              <Button
                type="text"
                size="small"
                icon={<ReloadOutlined spin={loadingStats} />}
                onClick={loadMonthlyStats}
                disabled={loadingStats}
                style={{ color: '#1890ff' }}
              >
                Refresh Stats
              </Button>
            )}
          </div>
          <Button
            type="dashed"
            size="small"
            icon={<PlusOutlined />}
            onClick={handleAddMonthCap}
          >
            Add Month
          </Button>
        </div>

        {localMonthlyCaps.length === 0 ? (
          <div style={{ 
            padding: '24px', 
            textAlign: 'center', 
            color: '#888',
            background: '#fafafa',
            borderRadius: '4px'
          }}>
            <FontAwesomeIcon icon={faCalendarAlt} style={{ fontSize: '24px', marginBottom: '8px', opacity: 0.5 }} />
            <p style={{ margin: 0, fontSize: '13px' }}>
              No month-specific caps set. Add a cap for specific months or enable the default cap above.
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* Sort caps chronologically by year then month */}
            {[...localMonthlyCaps]
              .map((cap, originalIndex) => ({ ...cap, originalIndex }))
              .sort((a, b) => {
                if (a.year !== b.year) return a.year - b.year;
                return a.month - b.month;
              })
              .map((cap) => {
              const index = cap.originalIndex; // Use original index for updates
              const stats = getMonthStats(cap.month, cap.year);
              const percentUsed = stats ? Math.round((stats.currentCount / cap.maxBookings) * 100) : 0;
              
              return (
                <div 
                  key={`${cap.year}-${cap.month}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px',
                    background: cap.isEnabled ? '#fff' : '#fafafa',
                    border: `1px solid ${cap.isEnabled ? '#e8e8e8' : '#d9d9d9'}`,
                    borderRadius: '6px',
                    opacity: cap.isEnabled ? 1 : 0.7
                  }}
                >
                  <Switch
                    checked={cap.isEnabled}
                    onChange={(checked) => handleUpdateMonthCap(index, 'isEnabled', checked)}
                    size="small"
                  />
                  
                  <div style={{ flex: '0 0 160px' }}>
                    <select
                      value={`${cap.year}-${cap.month}`}
                      onChange={(e) => {
                        const [year, month] = e.target.value.split('-').map(Number);
                        // Update both month and year together to avoid stale state
                        const updated = [...localMonthlyCaps];
                        updated[index] = { ...updated[index], month, year };
                        setLocalMonthlyCaps(updated);
                        onMonthlyCapChange?.(updated);
                        // Refresh stats for the new month selection
                        loadMonthlyStats();
                      }}
                      style={{
                        width: '100%',
                        height: '48px',
                        border: '1px solid #d9d9d9',
                        borderRadius: '6px',
                        padding: '0 11px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                        backgroundColor: cap.isEnabled ? '#fff' : '#f5f5f5',
                        cursor: cap.isEnabled ? 'pointer' : 'not-allowed'
                      }}
                      disabled={!cap.isEnabled}
                    >
                      {getAvailableMonthOptions(index).map(opt => (
                        <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                          {opt.label}{opt.disabled ? ' (already set)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div style={{ flex: '0 0 100px' }}>
                    <InputNumber
                      value={cap.maxBookings}
                      onChange={(value) => {
                        handleUpdateMonthCap(index, 'maxBookings', value);
                        // Debounced refresh so user sees updated progress in real-time
                        debouncedRefreshStats();
                      }}
                      min={1}
                      max={10000}
                      disabled={!cap.isEnabled}
                      style={{ width: '100%', height: '48px' }}
                    />
                  </div>
                  
                  {/* Stats column - shows current bookings vs cap */}
                  <div style={{ flex: 1, minWidth: '140px' }}>
                    {loadingStats ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Spin size="small" />
                        <span style={{ fontSize: '12px', color: '#888' }}>Loading...</span>
                      </div>
                    ) : (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Progress
                            percent={stats ? Math.min(percentUsed, 100) : 0}
                            size="small"
                            strokeColor={stats ? getProgressColor(percentUsed) : '#52c41a'}
                            showInfo={false}
                            style={{ flex: 1, margin: 0 }}
                          />
                          <span style={{ fontSize: '12px', color: '#666', whiteSpace: 'nowrap' }}>
                            {stats?.currentCount || 0}/{cap.maxBookings}
                          </span>
                        </div>
                        {stats && percentUsed >= 100 && (
                          <Tag color="red" style={{ fontSize: '10px', marginTop: '4px' }}>
                            Cap Reached
                          </Tag>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <Popconfirm
                    title="Remove this monthly cap?"
                    onConfirm={() => handleRemoveMonthCap(index)}
                    okText="Remove"
                    cancelText="Cancel"
                  >
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                    />
                  </Popconfirm>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Stats Summary (only for editing mode) */}
      {isEditing && monthlyStats?.stats && (
        <div style={{ 
          marginTop: '16px',
          padding: '16px', 
          background: 'white', 
          borderRadius: '8px',
          border: '1px solid #e8e8e8'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            marginBottom: '12px'
          }}>
            <FontAwesomeIcon icon={faChartLine} style={{ color: '#1890ff' }} />
            <span style={{ fontWeight: '500', color: '#1a1a1a' }}>
              Upcoming Months Overview
            </span>
            <Button size="small" type="link" onClick={loadMonthlyStats} loading={loadingStats}>
              Refresh
            </Button>
          </div>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', 
            gap: '8px' 
          }}>
            {monthlyStats.stats.slice(0, 6).map((stat) => (
              <div 
                key={`${stat.year}-${stat.month}`}
                style={{
                  padding: '12px',
                  background: stat.capReached ? '#fff2f0' : '#f6ffed',
                  borderRadius: '6px',
                  border: `1px solid ${stat.capReached ? '#ffccc7' : '#b7eb8f'}`,
                  textAlign: 'center'
                }}
              >
                <div style={{ fontWeight: '600', fontSize: '13px', color: '#1a1a1a' }}>
                  {stat.monthName} {stat.year}
                </div>
                {stat.hasCapEnabled ? (
                  <>
                    <div style={{ fontSize: '18px', fontWeight: '600', color: stat.capReached ? '#ff4d4f' : '#52c41a' }}>
                      {stat.currentCount}/{stat.limit}
                    </div>
                    <div style={{ fontSize: '11px', color: '#888' }}>
                      {stat.remaining} remaining
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: '12px', color: '#888' }}>
                    No cap set
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthlyCapSection;
